import { useMemo } from 'react';
import type { FloatingPosition, FloatingAlignment } from './useFloatingPosition';

export interface SmartPositionOptions {
  position: FloatingPosition;
  alignment: FloatingAlignment;
  triggerRect: DOMRect | null;
  contentWidth?: number;
  contentHeight?: number;
  gap?: number;
  enableFlip?: boolean;
}

export interface SmartPositionResult {
  position: FloatingPosition;
  alignment: FloatingAlignment;
  didFlip: boolean;
}

/**
 * Smart positioning that detects viewport boundaries and adjusts position/alignment
 * to keep content visible on screen
 */
export const useSmartPosition = ({
  position: initialPosition,
  alignment: initialAlignment,
  triggerRect,
  contentWidth = 200,
  contentHeight = 100,
  gap = 8,
  enableFlip = true,
}: SmartPositionOptions): SmartPositionResult => {
  return useMemo(() => {
    if (!triggerRect || !enableFlip) {
      return {
        position: initialPosition,
        alignment: initialAlignment,
        didFlip: false,
      };
    }

    let position = initialPosition;
    let alignment = initialAlignment;
    let didFlip = false;

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const padding = 16; // Minimum distance from viewport edge

    // Calculate where content would appear with current settings
    const contentRect = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: contentWidth,
      height: contentHeight,
    };

    // Position calculations
    switch (position) {
      case 'top':
        contentRect.bottom = triggerRect.top - gap;
        contentRect.top = contentRect.bottom - contentHeight;
        break;
      case 'bottom':
        contentRect.top = triggerRect.bottom + gap;
        contentRect.bottom = contentRect.top + contentHeight;
        break;
      case 'left':
        contentRect.right = triggerRect.left - gap;
        contentRect.left = contentRect.right - contentWidth;
        break;
      case 'right':
        contentRect.left = triggerRect.right + gap;
        contentRect.right = contentRect.left + contentWidth;
        break;
    }

    // Alignment calculations for top/bottom
    if (position === 'top' || position === 'bottom') {
      switch (alignment) {
        case 'start':
          contentRect.left = triggerRect.left;
          contentRect.right = contentRect.left + contentWidth;
          break;
        case 'center':
          contentRect.left = triggerRect.left + triggerRect.width / 2 - contentWidth / 2;
          contentRect.right = contentRect.left + contentWidth;
          break;
        case 'end':
          contentRect.right = triggerRect.right;
          contentRect.left = contentRect.right - contentWidth;
          break;
      }
    }

    // Alignment calculations for left/right
    if (position === 'left' || position === 'right') {
      switch (alignment) {
        case 'start':
          contentRect.top = triggerRect.top;
          contentRect.bottom = contentRect.top + contentHeight;
          break;
        case 'center':
          contentRect.top = triggerRect.top + triggerRect.height / 2 - contentHeight / 2;
          contentRect.bottom = contentRect.top + contentHeight;
          break;
        case 'end':
          contentRect.bottom = triggerRect.bottom;
          contentRect.top = contentRect.bottom - contentHeight;
          break;
      }
    }

    // Check and fix position overflow
    if (position === 'top' && contentRect.top < padding) {
      // Not enough space on top, flip to bottom
      position = 'bottom';
      didFlip = true;
    } else if (position === 'bottom' && contentRect.bottom > viewport.height - padding) {
      // Not enough space on bottom, flip to top
      position = 'top';
      didFlip = true;
    } else if (position === 'left' && contentRect.left < padding) {
      // Not enough space on left, flip to right
      position = 'right';
      didFlip = true;
    } else if (position === 'right' && contentRect.right > viewport.width - padding) {
      // Not enough space on right, flip to left
      position = 'left';
      didFlip = true;
    }

    // Check and fix alignment overflow for top/bottom positions
    if (position === 'top' || position === 'bottom') {
      if (contentRect.left < padding) {
        // Overflowing left edge, align to start
        alignment = 'start';
        didFlip = true;
      } else if (contentRect.right > viewport.width - padding) {
        // Overflowing right edge, align to end
        alignment = 'end';
        didFlip = true;
      }
    }

    // Check and fix alignment overflow for left/right positions
    if (position === 'left' || position === 'right') {
      if (contentRect.top < padding) {
        // Overflowing top edge, align to start
        alignment = 'start';
        didFlip = true;
      } else if (contentRect.bottom > viewport.height - padding) {
        // Overflowing bottom edge, align to end
        alignment = 'end';
        didFlip = true;
      }
    }

    return {
      position,
      alignment,
      didFlip,
    };
  }, [initialPosition, initialAlignment, triggerRect, contentWidth, contentHeight, gap, enableFlip]);
};
